
// Vue reactivity
import { computed, reactive, ref, watch } from 'vue';

// icons
import { checkmark, close, chevronBack, chevronForward, logoWhatsapp, shareSocialOutline, downloadOutline,
        swapHorizontal, chevronUp, chevronDown, eyeOutline, eyeOffOutline,
        arrowUndoOutline, arrowRedoOutline, cameraOutline, colorWandOutline, createOutline, add, text, scan,
        ellipsisVertical, ellipsisHorizontal, folderOutline, copyOutline, duplicateOutline, trashOutline, crop,
        removeCircleOutline, remove, arrowForward, arrowBack, } from 'ionicons/icons';

// components
import { IonPage, IonGrid, IonRow, IonCol, IonHeader, IonToolbar, IonTitle, IonLabel, IonContent, IonChip,
        IonBackButton, IonButtons, IonButton, IonIcon, IonSegment, IonSegmentButton, IonRange,
        IonSpinner, IonList, IonCard, IonThumbnail, IonItem, IonPopover, IonSelect, IonSelectOption,
        IonBadge, IonText, IonModal, IonToggle, IonProgressBar,
        loadingController, isPlatform, onIonViewDidEnter, alertController, modalController, onIonViewWillLeave, } from '@ionic/vue';
import StickerListModal from '@/components/modals/StickerListModal.vue';
import StickerPreQuestionModal from '@/components/modals/StickerPreQuestionModal.vue';
import DIYProductModal from '@/components/modals/DIYProductModal.vue';

import ColorPicker from 'simple-color-picker';
import { VueDraggableNext } from 'vue-draggable-next';
import Konva from 'konva';

// composables
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { utils } from '@/composables/utils';
import { utilsDesign } from '@/composables/utilsDesign';
import { usePhotoGallery, Photo } from '@/composables/usePhotoGallery';
import { useKonvaCustomFilters } from '@/composables/useKonvaCustomFilters';
import { Directory } from '@capacitor/filesystem';

// services
import StickerService from '@/services/StickerService';

// CSS
import '@/theme/diy.css';

export default {
  name: 'StickerDetailPage',
  components: { IonPage, IonGrid, IonRow, IonCol, IonHeader, IonToolbar, IonTitle, IonLabel, IonContent, IonChip,
                IonBackButton, IonButtons, IonButton, IonIcon, IonSegment, IonSegmentButton, IonRange,
                IonSpinner, IonList, IonCard, IonThumbnail, IonItem, IonPopover, IonSelect, IonSelectOption,
                IonBadge, IonText, IonModal, IonToggle, IonProgressBar,
                draggable: VueDraggableNext },
  setup() {
    Konva.hitOnDragEnabled = true; // detect gesture

    // methods or composables
    const { t } = useI18n();
    const route = useRoute();
    const router = useRouter();
    const { sleep, arraymove, presentToast, openModal, presentPrompt, scrollToRefElement,
            loadHTMLImg, saveImageToDevice, shareImage, readFileFromDevice, writeFileToDevice,
            uniqueId, getCorner, getClientRect, getLocalizedStr, isNumber, getBase64FromUrl,
            addResizeUrlParams, } = utils();
    const { photoFilters, setPanelModalBreakpoint, getCenter, getDistance, isPinching, addFiltersToShape,
            getDBCanvasData, checkAttachTransformer, clearTransformerSelection, onTrDragMove, refreshLineGuides,
            initMaterials, getShapeDataURL, getTrimmedCanvasDataURL, clearKonvaDOMCanvases, resetCustomShapeFilterState,
            navigateMaterialCategories, convertCanvasToImg,
            FONT_FAMILY_OPTIONS, OUTPUT_STICKER_WIDTH, } = utilsDesign();
    const { takePhoto, } = usePhotoGallery();
    const { Border, SoftImageEdge } = useKonvaCustomFilters();

    // Vuex Store
    let { id: currStickerId } = route.params; // for editing existing sticker
    const store = useStore();
    const sticker = currStickerId ? computed(() => store.getters.getStickerById(currStickerId)) :
                                  computed(() => store.state.newSticker);
    const loadingUserStickers = computed(() => store.state.loadingUserStickers);

    // State variables
    const dummy = ref("dummy"); // for segment buttons (no highlight select)
    const materials = computed(() => store.state.materials);
    const materialCategories = computed(() => store.state.materialCategories);
    const settings = computed(() => store.state.settings);

    // Popver
    const popoverState = reactive({
      menu: false,
      textColor: false,
    })
    const popoverEvent = ref();
    const isColorPopoverOpenRef = ref(false);
    const colorPopoverEvent = ref();

    // Canvas Configutations / Helpers
    let colorPicker;
    let loadCanvasTimeout: any = null;

    const filterStageRef = ref(null), stageRef = ref(null);
    const transformer = ref(null);
    const screenWidth = Math.min(window.innerWidth-10, 400);

    const canvas = reactive({
      designLayerScale: 1,
      designLayerX: 0,
      designLayerY: 0,
      sceneWidth: screenWidth,
      sceneHeight: screenWidth,
      loading: true,
      isFlipping: false,
      isDragging: false,
      isPinching: false,
      isSelectingBackground: false,

      selectedPanelShape: { colors: [] },
      //selectedSectionTab: '已選',
      selectedSectionTab: materialCategories.value[0]?.id,

      layerVisibility: {
        'image': true,
        'accessories': true,
        'text': true,
      },

      // Line Guides
      lineGuides: [],

      // Image
      customImgPrefix: 'custom_',
      defaultImgWidth: 150,
      defaultImgHeight: 150,
      targetImgAttr: 'brightness',
      
      // Text
      colorChangeTarget: 'color', // color / stroke
      defaultStrokeWidth: 1,
      defaultStrokeColor: 'rgb(255,255,255)',
      defaultFontFamily: '中文娃娃體1',
      defaultFontColor: 'rgb(0,0,0)',
      fontFamilyOptions: FONT_FAMILY_OPTIONS,

      // Eraser
      isErasing: false,
      //erasedPoints: [],
      erasingLines: [],
      erasingShape: { id: '', scale: 1 },
      eraserRadius: 10,
      minEraserHelperRadius: 60,

      // Shapes
      shapes: [],
      panelShapes: [],
      selectedShape: { id: '' },
      lockMoveSelectedShape: false,

      // Filters
      selectedFilter: '', // default filter
      isFiltering: false,
      filteringImg: {
        image: null,
        scale: 1,
        pixelSize: 3,
        levels: -0.05,
      },

      // Background
      backgroundMaterialId: null,
      backgroundImage: null,
      backgroundImageScale: 1,
      backgroundImageX: 0,
      backgroundImageY: 0,
    });

    // eraser
    let eraserNode, eraserGroupNode, erasedImgNode, erasedImgGroupNode;
    let lastSavedErasingLines = [];
    const autoSavePointCount = 100; // auto save during drag

    // gesture (single / two finger moves)
    let lastDist = 0, lastCenter = null, singleFingerLastPoint = null;
    let pointOffsetX = 0, pointOffsetY = 0; // for erasing lines (after scaling)

    // undo / redo
    const appHistory = reactive({
      step: -1,
      states: [],
    })
    const eraserHistory = reactive({
      step: -1,
      states: [],
    })
    const saveStateToHistory = (state: any) => {
      appHistory.states = appHistory.states.slice(0, appHistory.step + 1);
      appHistory.states.push(state);
      appHistory.step += 1;
    }
    const saveStateToEraserHistory = () => {
      const state = {
        action: 'addErasingLines',
        prevErasingLines: [...lastSavedErasingLines],
        nextErasingLines: [...canvas.erasingLines],
      };
      lastSavedErasingLines = [...canvas.erasingLines];
      eraserHistory.states = eraserHistory.states.slice(0, eraserHistory.step + 1);
      eraserHistory.states.push(state);
      eraserHistory.step += 1;
    }

    /**
     * Transformer functions
     */
    const updateTransformer = (forceAttach = false) => {
      checkAttachTransformer(transformer.value, canvas.selectedShape, forceAttach);
    }
    const setCanvasSelectedShape = async (shape: any, lockMoveShape = false, fromPanel = false) => {
      const panelModal: any = document.querySelector('#panel-modal');
      canvas.isErasing = false;
      canvas.isSelectingBackground = false;

      if (canvas.selectedShape.id == shape.id) {
        return; // already is the selected shape)
      }
      canvas.selectedShape = shape;
      canvas.lockMoveSelectedShape = lockMoveShape;
      updateTransformer();
      if (shape.id) {
        if (!shape.materialId && shape.type == 'text') {
          canvas.colorChangeTarget = 'color';
          if (!fromPanel) panelModal.setCurrentBreakpoint(0.3);
        }
      } else if (!fromPanel) {
        const currBreakPoint = await panelModal.getCurrentBreakpoint();
        panelModal.setCurrentBreakpoint(Math.min(currBreakPoint, 0.4));
      }
      if (fromPanel) {
        panelModal.setCurrentBreakpoint(shape.materialId ? 0.4 : 0.6);
      }
    }

    const getCanvasDataURL = (mimeType = "image/png", pixelRatio = 1, extraOptions = {}) => {
      if (canvas.selectedShape.id) clearTransformerSelection(transformer.value); // clear transformer selection
      const targetNode = canvas.isFiltering ? filterStageRef.value.getStage() : (stageRef.value ? stageRef.value.getStage() : null);
      const dataUrl = targetNode ? targetNode.toDataURL({ mimeType, pixelRatio, ...extraOptions }) : "";
      if (canvas.selectedShape.id) updateTransformer();
      return dataUrl;
    }
    // BUG FIX: sticker fixed width 512 x 512
    const getCanvasStickerDataURL = () => {
      if (canvas.selectedShape.id) clearTransformerSelection(transformer.value); // clear transformer selection
      const targetNode = canvas.isFiltering ? filterStageRef.value.getStage() : (stageRef.value ? stageRef.value.getStage() : null);
      let dataUrl = "";
      if (targetNode) {
        const pixelRatio = Math.round(((OUTPUT_STICKER_WIDTH / canvas.sceneWidth)+ Number.EPSILON) * 100000000000) / 100000000000;
        const outputCanvas = targetNode.toCanvas({ pixelRatio });
        dataUrl = convertCanvasToImg(outputCanvas, OUTPUT_STICKER_WIDTH, OUTPUT_STICKER_WIDTH);
      }
      if (canvas.selectedShape.id) updateTransformer();
      return dataUrl;
    }

    /**
     * Canvas Save Functions
     */
    let serverReqFlag: any = null; // auto save canvas server request

    const syncUserStickerData = (newStickerName: any = null) => {
      const outputPhotoBase64 = getCanvasStickerDataURL();
      const updatedObj: any = {
        canvasJson: getDBCanvasData(canvas),
        updatedAt: new Date(),
      };
      if (newStickerName) updatedObj.name = newStickerName;
      StickerService.upsertUserSticker({
        ...sticker.value,
        ...updatedObj,
        outputPhoto: outputPhotoBase64
      });
      store.commit('updateSticker', { stickerId: sticker.value.id, updatedObj: { canvas, ...updatedObj, outputPhotoBase64 } });
      serverReqFlag = null;
    }
    const autoSaveCanvas = (timeout = 4000) => {
      if (sticker.value.id) { // editing existing sticker: save to DB directly
        if (canvas.shapes.length == 0) return; // not save canvas if shapes not fully loaded

        if (serverReqFlag) {
          clearTimeout(serverReqFlag);
          serverReqFlag = null;
        }
        serverReqFlag = setTimeout(syncUserStickerData, timeout);
      }
    }

    /**
     * Save / load canvas shapes
     */
    const setCanvasLoaded = (timeout = 3000) => {
      if (loadCanvasTimeout) {
        clearTimeout(loadCanvasTimeout);
        loadCanvasTimeout = null;
      }
      canvas.loading = true; // bug fix prevent empty canvas
      loadCanvasTimeout = setTimeout(() => {
        canvas.loading = false;

        // add brighten filters to custom images
        setTimeout(() => {
          for (const shape of canvas.shapes) {
            if (shape.id.startsWith(canvas.customImgPrefix)) {
              const pixelRatio = Math.min(OUTPUT_STICKER_WIDTH*2 / shape.loadedImg.naturalWidth, 1);
              addFiltersToShape(stageRef.value, shape, [Konva.Filters.Brighten, SoftImageEdge], pixelRatio);
            }
          }
          updateTransformer();
          eraserNode = stageRef.value.getStage().findOne('#eraser');
          eraserGroupNode = stageRef.value.getStage().findOne('#eraserGroup');
          erasedImgNode = stageRef.value.getStage().findOne('#erasedImg');
          erasedImgGroupNode = stageRef.value.getStage().findOne('.erasedImgGroup');
        }, 150);

        loadCanvasTimeout = null;
        //autoSaveCanvas();
      }, timeout);
    }
    const saveNewSticker = async (name: any) => {
      const payload = {
        ...sticker.value,
        newStickerId: uniqueId(),
        name,
        canvasJson: getDBCanvasData(canvas),
      }
      delete payload['id'];

      // Insert Sticker Record to DB
      const res = await StickerService.upsertUserSticker(payload);

      // Update Store Sticker
      const latestNewSticker = {
        ...sticker.value,
        id: res.id,
        name,
        canvasJson: payload.canvasJson,
        canvas: { ...canvas },
      };
      store.commit('receiveNewUserSticker', latestNewSticker);
      store.commit('upsertUserSticker', { ...res, ...latestNewSticker });

      // Change the URL
      window.history.replaceState({}, document.title, `/stickers/${res.id}`);
      currStickerId = res.id;
    }
    const presentStickerNameInput = async (callback: any, prefilledName = "") => {
      const alert = await alertController.create({
        header: t('enterStickerName'),
        inputs: [
          {
            name: 'stickerName',
            type: 'text',
            placeholder: t('stickerName'),
            value: prefilledName || sticker.value.name || "",
          },
        ],
        buttons: [
          {
            text: t('cancel'),
            role: 'cancel',
            cssClass: 'secondary',
          }, {
            text: prefilledName ? t('makeACopy') : t('save'),
            handler: (value) => {
              if (value.stickerName) {
                callback(value.stickerName);
              }
              return false;
            }
          }
        ]
      });
      await alert.present();
    }
    const saveCanvas = async () => {
      const { id: stickerId } = sticker.value;
      if (stickerId) {
        syncUserStickerData(); // editing existing design
        return true;
      } else {
        return new Promise((resolve) => {
          presentStickerNameInput(async (name: any) => {
            const loading = await loadingController.create({});
            await loading.present();
            try {
              await saveNewSticker(name); // create new sticker
              presentToast(t('saveSuccess'));
              resolve(true);
            } catch (e) {
              presentToast(t('saveFail'));
              resolve(false);
            } finally {
              alertController.dismiss();
              loading.dismiss();
            }
          });
        })
      }
    }

    /**
     * Shape Functions
     * addShape: Add shape to specific index
     * removeShape: Remove shape of ID from canvas
     * Recover: shape attrs (x, y, rotation, scale)
     * updateShapeLayerOrder: Move shape to specific order in the layer
     */
    const addShape = (shape: any, shapeIdx: any, panelShapeIdx: any, setShapeSelected = true, saveHistory = false) => {
      if (shape.type == 'text') {
        shape = {
          color: canvas.defaultFontColor,
          fontFamily: canvas.defaultFontFamily,
          strokeWidth: canvas.defaultStrokeWidth,
          strokeColor: canvas.defaultStrokeColor,
          fontStyle: '',
          ...shape,
        }
      }
      const { designLayerX, designLayerY, designLayerScale } = canvas;
      if (!('x' in shape) || !isNumber(shape.x)) shape.x = (10-designLayerX)/designLayerScale;
      if (!('y' in shape) || !isNumber(shape.y)) shape.y = (10-designLayerY)/designLayerScale;
      if (!('scaleX' in shape)) shape.scaleX = 1/designLayerScale;
      if (!('scaleY' in shape)) shape.scaleY = 1/designLayerScale;
      canvas.panelShapes.splice(panelShapeIdx, 0, shape);
      canvas.shapes.splice(shapeIdx, 0, shape);
      if (setShapeSelected) {
        setTimeout(setCanvasSelectedShape, 100, shape);
      }
      if (saveHistory) {
        saveStateToHistory({ action: 'addShape', shape: { ...shape } });
      }
      autoSaveCanvas();
    }

    const removeShape = (shapeId: any, saveHistory = false) => {
      const panelShapeIdx = canvas.panelShapes.findIndex(s => s.id == shapeId);
      const shapeIdx = canvas.shapes.findIndex(s => s.id == shapeId);
      canvas.panelShapes.splice(panelShapeIdx, 1); // panel shape (first element is on the top of layer)
      const removedShapes = canvas.shapes.splice(shapeIdx, 1); // canvas shape (last element is on the top of layer)
      if (saveHistory) {
        saveStateToHistory({
          action: 'removeShape',
          shape: { ...removedShapes[0] },
          panelShapeIdx,
          shapeIdx,
        });
        autoSaveCanvas();
      }
      if (shapeId == canvas.selectedShape.id) setCanvasSelectedShape({ id: '' });
    }
    const recoverShape = (shape: any) => {
      const relatedShapeIdx = canvas.shapes.findIndex((s) => s.id == shape.id);
      canvas.shapes[relatedShapeIdx] = { ...shape };
      if (shape.id == canvas.selectedShape.id) setCanvasSelectedShape(canvas.shapes[relatedShapeIdx]);
    }
    const updateShapeLayerOrder = (oldShapeArrIdx: any, newShapeArrIdx: any, oldPanelShapeArrIdx: any, newPanelShapeArrIdx: any) => {
      arraymove(canvas.shapes, oldShapeArrIdx, newShapeArrIdx);
      arraymove(canvas.panelShapes, oldPanelShapeArrIdx, newPanelShapeArrIdx);
    }
    const updateShapeAttrs = (action: any = '', shapeId: any, attrs: any = {}, saveHistory = true) => {
      const shapeIdx = canvas.shapes.findIndex((s) => s.id == shapeId);
      const prevShape = { ...canvas.shapes[shapeIdx] };
      let someAttrsUpdated = false;
      for (const key in attrs) {
        if (canvas.shapes[shapeIdx][key] != attrs[key]) someAttrsUpdated = true;
        canvas.shapes[shapeIdx][key] = attrs[key];
      }
      if (someAttrsUpdated) {
        if (saveHistory) {
          saveStateToHistory({ action, prevShape, nextShape: { ...canvas.shapes[shapeIdx] } });
        }
        autoSaveCanvas(3000);
      }
    }

    const addCustomImageShape = async (photoDataURL: any, rembgPhotoDataURL: any, saveToHistory = true) => {
      const shapeId = `${canvas.customImgPrefix}${uniqueId()}`;
      const canvasImgShape: any = {
        type: 'image',
        id: shapeId,
        visible: true,
        useRembgPhoto: rembgPhotoDataURL ? true : false,
      };
      await writeFileToDevice(shapeId, photoDataURL, Directory.Data);
      if (rembgPhotoDataURL) await writeFileToDevice(`${shapeId}_rembg`, rembgPhotoDataURL, Directory.Data);

      loadHTMLImg(rembgPhotoDataURL || photoDataURL, (img: any) => {
        canvasImgShape.loadedImg = img;
        setTimeout(() => {
          const scale = Math.min(canvas.defaultImgWidth / img.naturalWidth, canvas.defaultImgHeight / img.naturalHeight);
          canvasImgShape.scaleX = scale;
          canvasImgShape.scaleY = scale;
          addShape(canvasImgShape, canvas.shapes.length, 0, true, saveToHistory);
          setCanvasLoaded(1000);
        }, 100)
      });
    }

    /**
     * Canvas loading
     */
    const addShapeToCanvas = (shape: any) => {
      if (!('x' in shape) || !isNumber(shape.x)) shape.x = (10-canvas.designLayerX)/canvas.designLayerScale;
      if (!('y' in shape) || !isNumber(shape.y)) shape.y = (10-canvas.designLayerY)/canvas.designLayerScale;
      if (!('scaleX' in shape)) shape.scaleX = 1/canvas.designLayerScale;
      if (!('scaleY' in shape)) shape.scaleY = 1/canvas.designLayerScale;
      canvas.shapes.push(shape);
      canvas.panelShapes.unshift(shape);
    }
    const loadSavedCanvas = async () => {
      const promises = []; // for loading images
      const { canvasJson, canvas: canvasData, customPhoto, rembgCustomPhoto, } = sticker.value;
      if (canvasJson) {
        const { shapes, backgroundMaterialId, backgroundImageScale, backgroundImageX, backgroundImageY,
                designLayerX, designLayerY, designLayerScale, } = canvasData;
        // Design Layer
        canvas.designLayerScale = designLayerScale || 1;
        canvas.designLayerX = designLayerX || 0;
        canvas.designLayerY = designLayerY || 0;

        // Background
        if (backgroundMaterialId) {
          const relatedMaterial = materials.value.find((m: any) => m.id == backgroundMaterialId);
          if (relatedMaterial) {
            setTimeout(() => {
              const { loadedImg: img } = relatedMaterial;
              canvas.backgroundImage = img;
              canvas.backgroundImageScale = backgroundImageScale || Math.max(canvas.sceneWidth / img.naturalWidth, canvas.sceneHeight / img.naturalHeight);
              canvas.backgroundImageX = backgroundImageX || 0;
              canvas.backgroundImageY = backgroundImageY || 0;
              canvas.backgroundMaterialId = backgroundMaterialId;
            }, 2000);
          }
        }
        if (shapes) { // add back the shape to canvas
          for (const shape of shapes) {
            const existingShape = canvas.shapes.find((s) => s.id == shape.id);
            if (existingShape == null) {
              shape.visible = true;
              if (shape.materialId) {
                const material = materials.value.find((m: any) => m.id == shape.materialId);
                if (material) { // emojis / accessories / clothes / ...
                  let color = {};
                  if (shape.color && material.colors) {
                    color = material.colors.find((c: any) => c.color == shape.color) || {};
                  }
                  addShapeToCanvas({ ...shape, ...material, ...color, id: shape.id });
                }
              } else {
                if (shape.type == 'image' && shape.id.startsWith(canvas.customImgPrefix)) {
                  try {
                    const parentShapeId = shape.id.split("::")[0];
                    const file = await readFileFromDevice(shape.useRembgPhoto ? `${parentShapeId}_rembg` : parentShapeId);
                    promises.push(
                      new Promise((resolve, reject) => {
                        loadHTMLImg(`data:image/webp;base64,${file.data}`, (img: any) => {
                          shape.loadedImg = img;
                          resolve(true);
                        })
                      })
                    );
                  } catch (e) {
                    console.log(e.message);
                    console.error(e); // image not found
                  }
                }
                addShapeToCanvas(shape);
              }
            }
          }
          //autoSaveCanvas(10);
        }
      } else if (customPhoto) { // first image uploaded by users
        addCustomImageShape(customPhoto, rembgCustomPhoto, false);
      }
      Promise.all(promises).then(res => {
        setCanvasLoaded(2000);
      });
    }

    /**
     * Canvas / material event listeners
     */
    let pinched = false;
    const resetEraseImage = () => {
      lastDist = 0;
      lastCenter = null;
      pointOffsetX = 0;
      pointOffsetY = 0;

      canvas.erasingLines = [];
      canvas.erasingShape = { id: '', scale: 1 };
      canvas.isErasing = false;
      lastSavedErasingLines = [];
      eraserHistory.step = -1;
      eraserHistory.states = [];
      erasedImgGroupNode.scaleX(1);
      erasedImgGroupNode.scaleY(1);
      erasedImgGroupNode.position({ x: undefined, y: undefined });
      if (serverReqFlag) autoSaveCanvas();
    }

    let lastSelectShapeTime = 0;
    const handleStageMouseDown = async (e: any) => {
      //if (canvas.isSelectingBackground && isPinching(e)) return;
      if (isPinching(e)) return;
      if (canvas.isErasing) {
        if (isPinching(e)) return; // scaling the images
        try {
          // Eraser mode
          const strokeWidth = (canvas.eraserRadius * 2) / canvas.erasingShape.scale;
          if (e.target.getParent() && e.target.getParent().getId() == 'eraserGroup') {
            canvas.erasingLines = [...canvas.erasingLines, { points: [], strokeWidth }];
          } else {
            const { x, y } = stageRef.value.getStage().getPointerPosition();
            eraserGroupNode.setAttrs({ x, y });
            canvas.erasingLines = [...canvas.erasingLines, { points: [], strokeWidth }];
            saveStateToEraserHistory();
          }
        } catch (e) { return; }
      } else {
        if (canvas.isPinching) return;
        // clicked on stage - clear selection
        try {
          if (e.target === e.target.getStage() || e.target.getLayer().name() != 'designLayer') {
            await sleep(0.05);
            if (!canvas.isPinching) setCanvasSelectedShape({ id: '' });
            return;
          }
        } catch (e) { return; }

        const clickedOnTransformer = (e.target.getParent().className === 'Transformer');
        if (clickedOnTransformer) return;

        if (canvas.lockMoveSelectedShape) {
          if (e.target.getId() != canvas.selectedShape.id) {
            setCanvasSelectedShape({ id: '' }); // release lock if click empty area
            return;
          }
          canvas.isDragging = true; // if mouse up then it will be set to false
          await sleep(0.15);
          if (!canvas.isDragging) {
            setCanvasSelectedShape({ id: '' });
          }
          return;
        }
        
        const id = e.target.getId();
        /*
        let customImgNode;
        const isCustomImgFirst = e.target.attrs.category == 'clothes';

        if (isCustomImgFirst && stageRef.value) {
          // If clicked area includes overlapped pet shape then set it as selected
          const stage = stageRef.value.getStage();
          const { x: pointerX, y: pointerY } = stage.getPointerPosition();
          const customImgNodes = stage.find(node => (node.id().startsWith(canvas.customImgPrefix)));
          for (const node of customImgNodes) {
            const { x, y, width, height} = node.getClientRect();
            if (pointerX > x && pointerX < x+width && pointerY > y && pointerY < y+height) {
              id = node.id(); // select overlapping image
              customImgNode = node;
              break;
            }
          }
        }
        */

        // find clicked rect by its name
        if (!canvas.isPinching) {
          const shape = canvas.shapes.find((s) => s.id == id);
          setCanvasSelectedShape(shape || { id: '' }, false);
          lastSelectShapeTime = new Date().valueOf();
        }
/*
        if (isCustomImgFirst && id.startsWith(canvas.customImgPrefix)) {
          canvas.lockMoveSelectedShape = true;
          customImgNode.fire('click');
          if (e.target.getId() != id) {
            e.target.stopDrag();
            //customImgNode.startDrag();
          }
        }
        */
      }
    }
    const handleStageMouseMove = (e: any) => {
      canvas.isDragging = true;
      if (e && e.evt && e.evt.touches) {
        const [touch1, touch2] = e.evt.touches;
        if (touch1 && touch2) {
          // Two-finger move
          canvas.isPinching = true;
          const p1 = { x: touch1.clientX, y: touch1.clientY };
          const p2 = { x: touch2.clientX, y: touch2.clientY };
          const dist = getDistance(p1, p2);
          if (!lastDist) lastDist = dist;

          if (canvas.isSelectingBackground) {
            const newScale = (canvas.backgroundImageScale * dist) / lastDist;
            if (!lastCenter) {
              lastCenter = getCenter(p1, p2);
              return;
            }
            const newCenter = getCenter(p1, p2);
            const pointTo = {
              x: (newCenter.x - canvas.backgroundImageX) / newScale,
              y: (newCenter.y - canvas.backgroundImageY) / newScale,
            };
            canvas.backgroundImageScale = newScale;
            canvas.backgroundImageX = newCenter.x - pointTo.x * newScale + (newCenter.x - lastCenter.x);
            canvas.backgroundImageY = newCenter.y - pointTo.y * newScale + (newCenter.y - lastCenter.y);
            lastDist = dist;
            lastCenter = newCenter;
            pinched = true;
          }
          else {
            if (new Date().valueOf() - lastSelectShapeTime < 500) {
              // for better UX (TODO: fix lag problem)
              canvas.selectedShape = { id: '' };
              transformer.value.getNode().nodes([]);
            }
            const stage = stageRef.value.getStage();
            const targetShape: any = canvas.isErasing ? canvas.erasingShape : canvas.selectedShape;
            const targetNode = canvas.isErasing ? erasedImgGroupNode :
                              (canvas.selectedShape.id ? stage.findOne(`#${canvas.selectedShape.id}`) : stage.findOne('.designLayer'));
            if (targetNode) {
              const scale = (targetNode.scaleX() * dist) / lastDist;
              targetNode.scaleX(scale);
              targetNode.scaleY(scale);
              targetShape.scale = scale;

              if (!lastCenter) {
                lastCenter = getCenter(p1, p2);
                return;
              }
              const newCenter = getCenter(p1, p2);
              const pointTo = {
                x: (newCenter.x - targetNode.x()) / targetNode.scaleX(),
                y: (newCenter.y - targetNode.y()) / targetNode.scaleX(),
              };
              const newX = newCenter.x - pointTo.x * scale + (newCenter.x - lastCenter.x);
              const newY = newCenter.y - pointTo.y * scale + (newCenter.y - lastCenter.y);
              targetNode.position({ x: newX, y: newY });

              pointOffsetX = 0 - (newX / scale);
              pointOffsetY = 0 - (newY / scale);

              lastDist = dist;
              lastCenter = newCenter;
              pinched = true;
            }
          }
        } else if (touch1 && !canvas.selectedShape.id) {
          // Single-finger move
          const p1 = { x: touch1.clientX, y: touch1.clientY };
          if (!singleFingerLastPoint) {
            singleFingerLastPoint = p1;
            return;
          }
          canvas.backgroundImageX = canvas.backgroundImageX - (singleFingerLastPoint.x-p1.x);
          canvas.backgroundImageY = canvas.backgroundImageY - (singleFingerLastPoint.y-p1.y);
          singleFingerLastPoint = p1;
        }
      }
    }
    const handleStageTouchEnd = (e: any) => {
      lastDist = 0;
      lastCenter = null;
      singleFingerLastPoint = null;
      canvas.isDragging = false;
      canvas.isPinching = false;
      if (pinched) {
        if (canvas.selectedShape.id) {
          const targetNode = stageRef.value.getStage().findOne(`#${canvas.selectedShape.id}`);
          updateShapeAttrs('transform', canvas.selectedShape.id, {
            x: targetNode.x(),
            y: targetNode.y(),
            //rotation: e.target.rotation(),
            scaleX: targetNode.scaleX(),
            scaleY: targetNode.scaleY(),
          });
        } else {
          if (!canvas.isSelectingBackground) {
            const designLayerNode = stageRef.value.getStage().findOne('.designLayer');
            const newScale = designLayerNode.scaleX();
            canvas.eraserRadius = canvas.eraserRadius / (newScale / canvas.designLayerScale);
            canvas.designLayerX = designLayerNode.x();
            canvas.designLayerY = designLayerNode.y();
            canvas.designLayerScale = newScale;
            resetEraseImage();
          }
          autoSaveCanvas(3000);
        }
        pinched = false;
      }
    }
    const handleEraserDragMove = (e: any) => {
      if (canvas.isErasing) {
        if (isPinching(e)) return;
        //const { x, y } = stageRef.value.getStage().getPointerPosition();
        //const { x, y } = eraserNode.getAbsolutePosition();
        const { x, y } = e.target.attrs;
        const { scale } = canvas.erasingShape;
        const lastLine = canvas.erasingLines[canvas.erasingLines.length - 1];
        lastLine.points = lastLine.points.concat([x/scale+pointOffsetX, y/scale+pointOffsetY]);
        canvas.erasingLines.splice(canvas.erasingLines.length - 1, 1, lastLine);
        if (lastLine.points.length > autoSavePointCount) {
          saveStateToEraserHistory();
          canvas.erasingLines = [...canvas.erasingLines, { points: [x/scale+pointOffsetX, y/scale+pointOffsetY], strokeWidth: (canvas.eraserRadius * 2) / canvas.erasingShape.scale }];
        }
      }
    }
    const handleEraserDragEnd = (e: any) => {
      if (canvas.isErasing) saveStateToEraserHistory();
    }

    const handleTransformEnd = (e: any) => {
      updateShapeAttrs('transform', canvas.selectedShape.id, {
        x: e.target.x(),
        y: e.target.y(),
        rotation: e.target.rotation(),
        scaleX: e.target.scaleX(),
        scaleY: e.target.scaleY(),
      });
      canvas.lineGuides = [];
    }
    const handleTransform = (e: any) => {
      refreshLineGuides(e.target, [e.target], stageRef, canvas);
    }
    const handleTrDragMove = (e: any) => {
      onTrDragMove(e, stageRef, canvas, transformer);
    };
    const handleTrDragEnd = (e: any) => {
      const tr = transformer.value.getNode();
      tr.nodes().forEach((shape: any) => {
        updateShapeAttrs('move', shape.id(), {
          x: shape.x(),
          y: shape.y(),
        });
      });
      canvas.lineGuides = [];
    };

    /**
     * Handle flowers retrieved from DB
     */
    const loadMaterialImages = async (materials: any) => {
      canvas.loading = true;
      await initMaterials(materials);
      loadSavedCanvas();
    }
    
    /**
     * INIT
     */
    onIonViewDidEnter(() => {
      if (canvas.loading == true) loadMaterialImages(materials.value);
    });
    onIonViewWillLeave(() => {
      if (serverReqFlag) autoSaveCanvas(0);
      if (colorPicker) colorPicker.remove(); // destroy color picker

      // clean-up canvases
      clearKonvaDOMCanvases(filterStageRef.value.getStage());
      clearKonvaDOMCanvases(stageRef.value.getStage());
      resetCustomShapeFilterState(canvas.shapes); // Reset filter status for custom shapes

      modalController.dismiss();
    });
    watch(materials, (currMaterials: any) => {
      loadMaterialImages(currMaterials);
      canvas.selectedSectionTab = materialCategories.value[0]?.id;
    });
    watch(sticker, (currSticker: any) => { // triggered only when direct access to this page
      if (currStickerId && !currSticker.id) {
        /*
        store.commit('setLoadingUserStickers', true);
        StickerService.getStickersByIds([currStickerId]).then(res => {
          store.commit('addNewUserStickers', res); // retrieve the sticker directly from DB
          canvas.loading = true;
        });
        */
      } else {
        loadSavedCanvas();
      }
    });

    // 3. return variables & methods to be used in template HTML
    return {
      // icons
      checkmark, close, chevronBack, chevronForward, logoWhatsapp, shareSocialOutline, downloadOutline,
      swapHorizontal, chevronUp, chevronDown, eyeOutline, eyeOffOutline, 
      arrowUndoOutline, arrowRedoOutline, cameraOutline, colorWandOutline, createOutline, add, text, scan,
      ellipsisVertical, ellipsisHorizontal, folderOutline, copyOutline, duplicateOutline, trashOutline,
      remove, arrowForward, arrowBack, crop, removeCircleOutline,

      // variables
      dummy, settings,
      sticker, loadingUserStickers,
      materials, materialCategories,
      appHistory, eraserHistory,

      // popover
      popoverState, popoverEvent,
      isColorPopoverOpenRef, colorPopoverEvent,

      // canvas
      canvas,
      stageRef, transformer,
      filterStageRef, photoFilters,

      // event listeners
      handleStageMouseDown, handleStageMouseMove, handleStageTouchEnd,
      handleEraserDragMove, handleEraserDragEnd,
      handleTransformEnd,
      handleTransform, handleTrDragMove, handleTrDragEnd,

      // methods
      t, getLocalizedStr, isNumber,
      getCanvasDataURL, addResizeUrlParams,
      setCanvasSelectedShape,
      saveCanvas,
      getCorner, getClientRect,
      removeShape,
      navigateMaterialCategories,

      isForDIYProduct: () => (history.state.isForDIYProduct == true),

      leavePage: async () => {
        await presentPrompt("", t("confirmLeave"), () => {
          router.replace(history.state.parentPath || "/home");
        });
      },

      reachedStickerLimit: () => (store.state.userStickers.length >= store.state.user.maxStickerCount),

      downloadSelectedImage: async () => (await saveImageToDevice(canvas.selectedShape['loadedImg'].src)),
      isSelectingCustomImage: () => (canvas.selectedShape.id || '').toString().startsWith(canvas.customImgPrefix),
      isSelectingCustomText: () => (canvas.selectedShape['type'] == 'text' && !canvas.selectedShape['materialId']),

      addFiltersToSelectedShape: () => {
        const shape: any = canvas.selectedShape;
        if (shape.loadedImg) {
          const pixelRatio = Math.min(OUTPUT_STICKER_WIDTH*2 / shape.loadedImg.naturalWidth, 1);
          addFiltersToShape(stageRef.value, shape, [Konva.Filters.Brighten, SoftImageEdge], pixelRatio);
        }
      },

      changeTextColor: (color: any) => {
        const [red, green, blue] = color.replace(/[^\d,]/g, '').split(',');
        updateShapeAttrs('changeColor', canvas.selectedShape.id, {
          [canvas.colorChangeTarget]: `rgb(${red},${green},${blue})`,
        });
      },

      onModalPresent: () => { // init color picker after modal presents
        if (colorPicker == null) {
          colorPicker = new ColorPicker({
            background: '#454545',
            el: '#picker',
            width: 208
          });
          colorPicker.onChange(() => {
            if (colorPicker.isChoosing) {
              const newRgb = colorPicker.getRGB();
              for (const key in newRgb) newRgb[key] = (Math.round(newRgb[key] * 1000) / 1000) * 255;
              updateShapeAttrs('changeColor', canvas.selectedShape.id, {
                [canvas.colorChangeTarget]: `rgb(${newRgb.r},${newRgb.g},${newRgb.b})`,
              });
            }
          });
        }
      },

      // Erase images
      startErasingImage: async () => {
        if (serverReqFlag) clearTimeout(serverReqFlag); // stop auto update
        canvas.erasingShape = canvas.shapes.find(s => s.id == canvas.selectedShape.id);
        canvas.erasingShape.scale = 1; // default scale (relative);
        setCanvasSelectedShape({ id: '' });
        canvas.isErasing = true;
        setPanelModalBreakpoint(0.3);
        lastSavedErasingLines = [{ points: [eraserNode.x(), eraserNode.y()], strokeWidth: canvas.eraserRadius * 2 / canvas.erasingShape.scale }];
      },
      resetEraseImage,
      completeEraseImage: async (erasingShape: any) => {
        //const { id, useRembgPhoto, loadedImg, x, y, scaleX, scaleY, } = erasingShape;
        const { id, useRembgPhoto, loadedImg, scale, scaleX, scaleY, x: imgX, y: imgY } = erasingShape;
        const { x: groupX, y: groupY } = erasedImgGroupNode.position();
        const x = groupX + (imgX * scale);
        const y = groupY + (imgY * scale);
        const finalScaleX = scaleX * scale, finalScaleY = scaleY * scale;
        const options = { pixelRatio: 1 / finalScaleX, x, y,  width: loadedImg.naturalWidth * finalScaleX, height: loadedImg.naturalHeight * finalScaleY };
        //const newImgDataURL = erasedImgGroupNode.toDataURL(options);
        const newImgDataURL = getTrimmedCanvasDataURL(erasedImgGroupNode.toCanvas(options), true);

        await writeFileToDevice(useRembgPhoto ? `${id}_rembg` : id, newImgDataURL, Directory.Data);
        loadHTMLImg(newImgDataURL, async (img: any) => {
          updateShapeAttrs('changeCustomImgSrc', id, { loadedImg: img }, true);
          resetEraseImage();
          setCanvasLoaded(100);
          autoSaveCanvas(); // sync latest image to server
        })
      },

      // add material to canvas
      addMaterialToCanvas: (material: any, color: any, ev: any) => {
        if (material.category == 'background') {
          setCanvasSelectedShape({ id: '' });
          const { id, loadedImg: img } = material;
          canvas.backgroundMaterialId = id;
          canvas.backgroundImage = img; // update the background image on canvas
          canvas.backgroundImageScale = Math.max(canvas.sceneWidth / img.naturalWidth, canvas.sceneHeight / img.naturalHeight);
          canvas.backgroundImageX = 0;
          canvas.backgroundImageY = 0;
          canvas.isSelectingBackground = true;
        } else {
          if (material.colors && material.colors.length > 0 && color == null) {
            scrollToRefElement(ev.target, 'auto');
            canvas.selectedPanelShape = material;
            isColorPopoverOpenRef.value = true;
            colorPopoverEvent.value = ev;
          } else {
            const canvasShape = { ...material, ...color, materialId: material.id, id: uniqueId(), visible: true };
            addShape(canvasShape, canvas.shapes.length, 0, true, true);
          }
        }
        autoSaveCanvas();
      },

      // draggable
      onChooseElement: (event: any) => {
        setCanvasSelectedShape(canvas.panelShapes[event.oldIndex], true);
      },
      doShapeReorder: (event: any) => {
        canvas.shapes = canvas.panelShapes.slice().reverse();
      },

      // Prompt
      openUpdateStickerNamePrompt: async() => {
        await presentStickerNameInput(async (name: any) => {
          const loading = await loadingController.create({});
          await loading.present();
          syncUserStickerData(name);
          alertController.dismiss();
          loading.dismiss();
        });
      },
      openTextPrompt: async (textShape: any = {}) => {
        const alert = await alertController.create({
          header: textShape.id ? t('editText') : t('addText'),
          inputs: [
            {
              name: 'text',
              type: 'textarea',
              placeholder: t('enterTextHere'),
              value: textShape.text || "",
            },
          ],
          buttons: [
            {
              text: t('cancel'),
              role: 'cancel',
              cssClass: 'secondary',
            }, {
              text: t('confirm'),
              handler: (value) => {
                if (value.text) {
                  if (textShape.id) { // edit text
                    if (textShape.text != value.text) {
                      updateShapeAttrs('updateText', textShape.id, { text: value.text });
                    }
                  } else { // new text
                    const newTextShape = { id: uniqueId(), type: 'text', text: value.text };
                    addShape(newTextShape, canvas.shapes.length, 0, true, true);
                  }
                  return true;
                }
                return false; // not closing the alert
              }
            }
          ]
        });
        await alert.present();
      },

      // Modal
      openNewCustomImagePrompt: async () => {
        const photo: Photo = await takePhoto();
        if (photo) {
          // get photo successfully
          const modal = await modalController.create({
            component: StickerPreQuestionModal,
            componentProps: { stickerId: sticker.value.id, petPhoto: photo.base64Data, mimeType: photo.mimeType },
          });
          modal.onDidDismiss().then(async ({ data }) => {
            if (data) {
              const { customPhoto, rembgCustomPhoto } = data;
              addCustomImageShape(customPhoto, rembgCustomPhoto);
            }
          });
          return modal.present();
        }
      },
      openStickerPreQuestionModal: async (stickerId: any, shapeId: any) => {
        const file = await readFileFromDevice(shapeId);
        const petPhoto = `data:image/webp;base64,${file.data}`;
        const modal = await modalController.create({
          component: StickerPreQuestionModal,
          componentProps: { stickerId, petPhoto, mimeType: 'image/webp' },
        });
        modal.onDidDismiss().then(async ({ data }) => {
          if (data) {
            const { customPhoto, rembgCustomPhoto } = data;
            await writeFileToDevice(shapeId, customPhoto, Directory.Data);
            if (rembgCustomPhoto) await writeFileToDevice(`${shapeId}_rembg`, rembgCustomPhoto, Directory.Data);
            loadHTMLImg(rembgCustomPhoto || customPhoto, (img: any) => {
              updateShapeAttrs('changeCustomImgSrc', shapeId, { loadedImg: img, useRembgPhoto: rembgCustomPhoto ? true : false }, true);
              setCanvasLoaded(100);
            });
          }
        });
        return modal.present();
      },
      openStickerListModal: async () => { await openModal(StickerListModal, { currStickerId }) },
      openDIYProductModal: async (sticker: any) => {
        const loading = await loadingController.create({});
        await loading.present();
        const { sceneWidth, sceneHeight, selectedShape, isFiltering } = canvas;
        if (canvas.selectedShape.id) clearTransformerSelection(transformer.value);
        const targetStage = isFiltering ? filterStageRef.value : stageRef.value;
        sticker.outputPhoto = getTrimmedCanvasDataURL(targetStage.getStage().toCanvas({ pixelRatio: OUTPUT_STICKER_WIDTH*2 / sceneWidth }), true, 75);
        if (selectedShape.id) updateTransformer(); // revert
        loading.dismiss();
        await openModal(DIYProductModal, { sticker, sceneWidth, sceneHeight });
      },

      cloneSticker: async (sticker: any) => {
        await presentStickerNameInput(async (name: any) => {
          const loading = await loadingController.create({});
          await loading.present();
          try {
            await saveNewSticker(name);
          } catch (e) {
            presentToast(t('errorCloneSticker'));
          } finally {
            alertController.dismiss();
            loading.dismiss();
          }
        }, `${sticker.name} 的副本`);
      },
      deleteSticker: async (stickerId: any) => {
        presentPrompt("", t("confirmDeleteSticker"), async () => {
          const loading = await loadingController.create({});
          await loading.present();
          StickerService.deleteUserSticker(stickerId);
          store.commit('deleteUserSticker', stickerId);
          loading.dismiss();
          presentToast( t('successDelete'), 2000 );
          router.replace('/home');
        });
      },
      
      setPopoverOpen: (popoverKey: any, state: boolean, ev: any) => {
        popoverEvent.value = ev; 
        popoverState[popoverKey] = state;
      },
        
      /**
       * UNDO / REDO (app history)
       */
      undo: (appHistory: any) => {
        if (appHistory.step === -1) return;

        const currState = appHistory.states[appHistory.step--];

        if (currState.action == 'updateShapeLayerOrder') {
          const { currShapeArrIdx, newShapeArrIdx, currPanelShapeArrIdx, newPanelShapeArrIdx } = currState;
          updateShapeLayerOrder(newShapeArrIdx, currShapeArrIdx, newPanelShapeArrIdx, currPanelShapeArrIdx);
        }
        else if (currState.action == 'addShape') {
          removeShape(currState.shape.id);
        }
        else if (currState.action == 'removeShape') {
          const { shape, panelShapeIdx, shapeIdx } = currState;
          addShape(shape, shapeIdx, panelShapeIdx);
        }
        else if (currState.action == 'addErasingLines') {
          const { prevErasingLines } = currState;
          canvas.erasingLines = [...prevErasingLines];
          lastSavedErasingLines = [...canvas.erasingLines];
          if (canvas.erasingLines.length > 0) {
            const [x, y] = canvas.erasingLines[canvas.erasingLines.length-1].points.slice(-2);
            if (x != null && y != null) eraserGroupNode.absolutePosition({ x, y });
          }
        }
        else if (currState.action == 'changeCustomImgSrc') {
          const { useRembgPhoto, id, loadedImg } = currState.prevShape;
          writeFileToDevice(useRembgPhoto ? `${id}_rembg` : id, loadedImg.src, Directory.Data);
          recoverShape(currState.prevShape);
        }
        else {
          recoverShape(currState.prevShape);
        }
      },
      redo: (appHistory: any) => {
        if (appHistory.step === appHistory.states.length-1) return;

        const currState = appHistory.states[++appHistory.step];

        if (currState.action == 'updateShapeLayerOrder') {
          const { currShapeArrIdx, newShapeArrIdx, currPanelShapeArrIdx, newPanelShapeArrIdx } = currState;
          updateShapeLayerOrder(currShapeArrIdx, newShapeArrIdx, currPanelShapeArrIdx, newPanelShapeArrIdx);
        }
        else if (currState.action == 'addShape') {
          addShape(currState.shape, canvas.shapes.length, 0);
        }
        else if (currState.action == 'removeShape') {
          removeShape(currState.shape.id);
        }
        else if (currState.action == 'addErasingLines') {
          const { nextErasingLines } = currState;
          canvas.erasingLines = [...nextErasingLines];
          lastSavedErasingLines = [...canvas.erasingLines];
          if (canvas.erasingLines.length > 0) {
            const [x, y] = canvas.erasingLines[canvas.erasingLines.length-1].points.slice(-2);
            if (x != null && y != null) eraserGroupNode.absolutePosition({ x, y });
          }
        }
        else if (currState.action == 'changeCustomImgSrc') {
          const { useRembgPhoto, id, loadedImg } = currState.nextShape;
          writeFileToDevice(useRembgPhoto ? `${id}_rembg` : id, loadedImg.src, Directory.Data);
          recoverShape(currState.nextShape);
        }
        else {
          recoverShape(currState.nextShape);
        }
      },
    
      // shape actions
      getShapeDataURL,
      toggleFontStyle: (style: any) => {
        if (canvas.selectedShape['type'] == 'text') {
          let newFontStyle = canvas.selectedShape['fontStyle'] || '';
          if (newFontStyle.includes(style)) {
            newFontStyle = newFontStyle.replace(style, '');
          } else {
            newFontStyle = `${newFontStyle} ${style}`;
          }
          canvas.selectedShape['fontStyle'] = newFontStyle.trim();
        }
      },
      duplicateSelectedShape: () => {
        const newShape = {
          ...canvas.selectedShape,
          x: canvas.selectedShape['x'] + 10,
          y: canvas.selectedShape['y'] + 10,
          id: `${canvas.selectedShape.id.split("::")[0]}::${uniqueId()}`,
          visible: true,
        };
        addShape(newShape, canvas.shapes.length, 0, true, true);
      },
      flipSelectedShape: () => {
        canvas.isFlipping = true;
        const targetNode = stageRef.value.getStage().findOne(`#${canvas.selectedShape.id}`);
        const shapeWidth = targetNode.width() * Math.abs(targetNode.scaleX());
        const newX = -targetNode.scaleX() < 0 ? targetNode.x() + shapeWidth : targetNode.x() - shapeWidth;
        targetNode.to({
          scaleX: -targetNode.scaleX(), // flip the shape on canvas
          x: newX,
          onFinish: () => canvas.isFlipping = false,
        });
        updateShapeAttrs('flip', canvas.selectedShape.id, { scaleX: -targetNode.scaleX(), x: newX });
      },
      moveSelectedShape: (direction: any) => { // update order of selected shape in the layer
        if (canvas.selectedShape.id) {
          const currShapeArrIdx = canvas.shapes.findIndex(s => s.id == canvas.selectedShape.id);
          const currPanelShapeArrIdx = canvas.panelShapes.findIndex(s => s.id == canvas.selectedShape.id);
          const lastArrIdx = canvas.shapes.length-1;
          let newShapeArrIdx, newPanelShapeArrIdx;
          if (direction == 'top') {
            newShapeArrIdx = lastArrIdx;
            newPanelShapeArrIdx = 0;
          }
          else if (direction == 'bottom') {
            newShapeArrIdx = 0;
            newPanelShapeArrIdx = lastArrIdx;
          }
          else if (direction == 'up') {
            newShapeArrIdx = Math.min(currShapeArrIdx+1, lastArrIdx);
            newPanelShapeArrIdx = Math.max(currPanelShapeArrIdx-1, 0);
          }
          else if (direction == 'down') {
            newShapeArrIdx = Math.max(currShapeArrIdx-1, 0);
            newPanelShapeArrIdx = Math.min(currPanelShapeArrIdx+1, lastArrIdx);
          }
          updateShapeLayerOrder(currShapeArrIdx, newShapeArrIdx, currPanelShapeArrIdx, newPanelShapeArrIdx);

          saveStateToHistory({
            action: 'updateShapeLayerOrder',
            shapeId: canvas.selectedShape.id,
            currShapeArrIdx,
            currPanelShapeArrIdx,
            newShapeArrIdx,
            newPanelShapeArrIdx,
          });
        }
      },
      setShapeVisible: (shape: any, visible: any) => {
        updateShapeAttrs('setShapeVisible', shape.id, { visible });
      },
      toggleLayerVisibility: (layerName: any) => {
        const newVisible = !canvas.layerVisibility[layerName]; // new visibility
        canvas.layerVisibility[layerName] = newVisible;
        if (canvas.selectedShape['type'] == layerName) setCanvasSelectedShape({ id: '' });
      },

      // Save / share sticker
      saveCanvasImg: async () => (await saveImageToDevice(getCanvasDataURL('image/png', 2))),
      shareCanvasImg: async () => (await shareImage(getCanvasDataURL())),

      // Preview / send to WhatsApp
      previewSticker: async () => {
        const alert = await alertController.create({
          header: t('stickerPreview'),
          message: `<img src="${getCanvasDataURL()}" />`,
          cssClass: 'sticker-preview-alert',
          buttons: [
            {
              text: t('back'),
              role: 'cancel',
              cssClass: 'secondary'
            },
          ],
        });
        return alert.present();
      },
      saveStickerToWhatsApp: async () => {
        const padStickers = (stickers: any) => {
          while (stickers.length < 3) {
            stickers.push(stickers[stickers.length-1]); // at least 3 stickers in the pack
          }
          return stickers;
        }
        await presentPrompt(t("confirmSaveStickerToWhatsApp"), `<img src="${getCanvasDataURL()}" />`, async () => {

          const loading = await loadingController.create({ duration: 20000 }); // timeout (ms)
          await loading.present();

          if (serverReqFlag) { // stop auto update
            clearTimeout(serverReqFlag);
          }

          try {
            // sticker tray icon
            const trayImgDataURL = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGAAAABgCAMAAADVRocKAAAC91BMVEUAAAAAAABjUUsQEBBeS0VaRkBuXVdWQjtoZ2cgIB9POzX7+/seBABDQ0MpKCgQEBAxMTH///8YFxckCwSUh4MrEwzd3d2kpKU6OjpFMCn///+0tbV2dnZgYGBTU1OJiYqDg4Nubm42IBn5+vvh4+PCw8OYmJh7fHxbWlr7/Pz09fZLS0sSAAA6JB34+frLy8uenp0yHBTZ2tvR0tLFxcW9vb26u7s/KiPl5eXm5uepqamtra6SkpI0HRcZAAD09PTt7e7x8/Pq7OzX19fGyMiATkB+bmmtra2sra03IR7///9kLx1iLRtnMB5lLx37+/r+/v79/f39/PuNUjred1v//v4nDgYkDAUqEAiRVTwhCAEeBABgLBowFw8tFQ2QVDs0GxOTVj2LUDhoMh83HhY9HhQtEwo6Ihr6+Ph6QiwlCgIaAQD08/KHeXV6amRfLyB1ZWCVVz1ELSYpEgutpKFTKhzv7ezg3NrMxcODdG9yQjA8Jh749vXq5+bn4+Lc19bPyMaQgn6WWD6CSjRzPCjV0c/RzMqITzhHMSpbLBwqGRH+9fDZ09KzqqeLfXlyYVzddFdpV1GDTTeHTDVNODBpPi1xOCZqNCFHKB3k4N/Buba5sKylm5eil5Tge1/mel2CRzF9RTBtNiNBIhjUzs3Gv7x+b2pNKx8vHBWxp6Sqn5zfUVJ3PytAKiJOJxtKJBn29PP029Lqy7abjorcb1Lba0xUPjZKNS5gOClXKx0lEQr87uidkY16RzRUMSMeDATx1MDywrW1rKk1Fw39+Pfx8O/Iwr/Cu7m9trS6s6/usqLkkHrxgmPZZERmLSf66OLz5t774Nnvuquhk4/rn4uXi4fRTU6gVkKNOjZcNSft6uj53Mf10MbFopPliG7hg2qSdmjoVFZoOSnezMbxyLziwa7uq5nYdFiXYUvVwrnVs6J/Y1bBaVCvX0nBSUmZPjuBPSqymI2vinmObF2hb1zvVVjPcFWyR0aSWkPl19KdgnRyVEfQUC3Fu3B5AAAASnRSTlMABP4M/v3+/mUg/vz8QikSMfkY/P781p45+/Kscl1ShX5r++3TuZJ4V/LnSf776cOZ+8vHwbax++HZpqOM+/rx5uLb07v8+6yo9eCimWsAAA+CSURBVGje7VplfFNXFG/S1N2VQg0bbgPG3E+TvEjjjTRJk7apu7u7e6GlXkpLKbSlOIxhw91l7u77sJukMjYgGePjzi9Jkyvn/46f+151/qf/aYpwiNDn1BvR02U+80P3L8NPi72SrbXl7Ne81i19bt3Cpau97L3nmRvhlHNPh72RmcMSZ4CS482dKU2t8Y1NTelAcfZyd7T472Kg7eZrrAD6y2ydbFgsGw8bUzs7LstGLzM0A2DhPNx/lsJtjTNWVmnMZXKl40lJ4wHX9jbsS0q6JmCx7PD6x+EVB/MnR0A7dd+C1D4DLl9wraq2KisyMlLMyRL6oy9JSdkdMhkzsZnxouOTIqB9uFUQaseauEbfS4/M4uS3tWfTs9vF1W3ZYoRVVVUlZ3Gd0jwN0dIn4+/oDfq8hqTafH9/elDQAIlIJNb7+EnI6E/7QJs/x39vUm20cRe4WDwBAtrhCnFlrH10/0j6QN1mP8Tdx8eH6ENW/SH6+eTUZWdlcfYJmImwDqnpX/O3cIC+51kNYo64egBxJ5PJPn8h9IvoJwlqp9Oze7gGjRS3f83f3B4ymdLaKk5bvQ9xmvk/MILoHM54tDQPluFw/yq6LNbG6XEbqvyF1UgrjySEK9mcnZUkiNoDDjq4fyPAXDBgjdOFdGEOEXF5DCHTtHF6RDxbmKU1Alo4B/qCxyOR79T7aCISWSKm5zcImsBQSwS0zAz6RUVJnKTwQrKERFIrnPxQ7mg2PDYIOaxIcBwstUTA4VbF8QNq6eMCNlvWERsukZCQW/o9aAuEiLiHxwawmYKOzWKEsDhwmVYAaNFsKGPVZvkHEJpG9AsEzMNDuyQ5AzmbH1A98tzC2ABmdEFETJlsV05+Vi2r6SVd7VSk63koWJ4kbD8cAQAhCbaE3h66ENnjr0raPJATLu8tSOwKAehiFpJJ4ioBAdxxOK1CAMLYVZHZPofxNIwKiEp+odODSA+oKKeak9X2/REACKQdYhaS/Nqz9kWneaPd2lggZSwA+ScpoAYDCgVhvMOhB/n5kB/0zvogOv27FIyCQQsznERsQzpqsUKOpI2LlrPGI4P8JEPyNAQAKe9/+kv1P0OM6LeZ/s0twDDIVAJIxNlSftMCa80SWHg2m+4TctqJkl3R7zIC4ddPqsX+Dws3EsoTn2wDDMplJB+yX3vkXhkBntEgApqeD3r8fE51PZEUzrwAkPJJW3tdHemhQVznL/7kfcAMDiMAYhCHE2BMmaUZYG7K8h4Ovd4PpRlZROCtH8Q5yCMfEcTZHHHbpyHyIYkSrh0BgBYADnF21yLFykuWBIz98I24+u+ZmkSasXRdNd3/Qm8sGiET6yIbPEJcNAM8t215g38QUQkwNC6kD0j+ftm7BpX8piByhEHSwkmAWu6IlcYeQPflQ6z7QrJKKeFiscSPSCKRSWT0IiEikxFArx+JjEZVv338crL2qQD8BvzFImPG43WEJo2gm53P2ayqi5vp9UQyqXCXT7iPT6FPbHhsbGEHUktDh0+sNHZXR2zhIEpJftntfmTV6nz/hmcprpoADAEvq+LUEcnKLSi8SIWi6MNsmYx9mMlkstiCWGLQtWg2k89koVdvrHJVtkpcMpKlYTHM06SiFSV2DfRqtdtIJERSoVSuP6pfiS83DguzvVBuEECsHiR0h+b1X8g7tGcrU44MnKMyCELy71meoqmw6S5pNd3rL64nTkVsuAwPqoREASVdYtcJZc0wSbmVRUMzPlXvX8uK8dTV4EUrMqJVcUyeAmBXUhFRqEDDUrrHpIWRu6JCMIyKXjQMwJgfPoWAQi2fXf74qoPm1qTye4T5kunQ6hAcAgyjIcKoqeyA+0lDgqPTAxjlWPTgNEC2kH4YTzHTALAiwbQ2a8CPPOX1AVEpMEVUW7k8GZ+nVplabSFbA6YB6oX5qCZoAnA4sjggv21GQ3wCDRpDwyLKw7o7AVJzUwFokKYeyAOMos/eRZoEIAnHma0LjDQAGAJhIimybhKBFMuMAYhZFIxoURkEAiL0WaoeSGQEMt6d0hGKNFRz0r005rpAZa5DgTYF0AS0wIyM3Ny8hG0MSOnqOxoCFJp6IAQA4kWTfkRETd5e7qUFFpoKPra4R1XA1BSQHP8OTKs8t0bGZkd1AQXU9M6Z3XGTRiATc+icWlbzyzgNAM8Anl0lpLdJVAKER79+8c7Jg8hlqOgN+OBkebKshkpR/saw83cqfG9HTVrZr54u7HgWm6sx2S05atNDp4tRokQAg0VXNlXs2HmRygCgoOLI5Uv53DAKDYnAgI92Vvj6brpfNKiyV04+na65IKBZd4hgIo8Oqkepktwh+mKTr+/GnQdVrkmlteqJDGy3qb7D8I6NvgjgC6lKBGI1amRRSZuvEcDaKpRZJRQL/dvCyZLBoi8RgG/FSQaDUpkAGLzL6wfktwRk348QgJJ6REQJ6mny6ZxsZpgW7aOFcytv3F8orJUKGgqlHT9tVALcQZYmGMdR0msmkhMgpbIAo8LFHSr+m+hFQ7Gx9dV0sbAnOm+dFm2LA+hFJ4lrBVEEgbzoPhIAAdymUShpNVK95aLqgCI9eXIuYHBCJcHHm77s5bOYDfn+nHwBXosOG5097NP5UraAnwERst6k93yVNjgBDAoEtjDbf9r08f3kPhQKDDi3QyXAe1cmjI8nRu/bW1XFTKSo4lhzVfYQRRjjP7j9Pp/ZcQWJsPNOMYMBNMA8rrz38XtteFCanAHnd+6o2HQlqYNfcq7YgBvMb7BpelmdrDU5aoxiz+4PQ26eOteKT55ACBd3I3aIK1UvHAEUdivztBLh5snb2fuji0LP/HE+hjCix9KDOSoBNFo5U5F74lQx7IaDH3WKRod3g4o/EiGG++V7XyiTB6gRIGPCIOPW2TMnh+HmwSNRTlRXbTRk5NzEHH3/IBSfKT54EkaTMaAyJtM1ps8dDK6kTaXrQEa/4iic+kA5decURNikzNUGwPsIgdmbByc2Vmy8Ca1cfSrGmE7/WEx3a8p0KqIxErj6IcPDBz84A+fOjcgMAJ1xtDgdlCnCPtt9cydynotYMv9TJXsUZ6iKMShqHAoNo1EYDCWEvuISrQIZ+wRAOVfap03za0YjiFJuf3DilG/FjmEIY926cbYYpgmV52kBGMXDN+7ySosrNm703TFMPfhtsl2qt2YAcwjlxaBMfHHjxvMAXbzPb2zffmD4zNmRzITi3buLi4t3F59tLms5e3b4wPbt27dGbYMTpyoqTp0P3HnumKL0Fc02wK1MW85O7GttLH4HKJAyJv32wI0D2w98zts/8fuHn33/+Xcf3t26n8W7i8ZubL/LzQQa7fzJkx8xgAGJpgn22tzEgQQnHk/RrXQeDMqCZaO/fYYudfDn8P0FW/dPREdNBFz9uaPmwIe37kbxRenqFiDEtiVGX6FVIKAjyAJIaOXrM2gANEqJaFSkYEaNFtX9uOUqX/T1V6eLTl/ecu9072hysCI5WNmVMagYHFJwFaaEuHWqgqk5Ema/AnK9SWPGpzS26I9Ji65u2XJZdPreva+Krt67vKWuSG4cVnq9WS0ABi2szOZ4sNfuBh5aM4vqNEZTIahRsJj9XyMAec/le18Xnd6yZcvp3nSYIWWti1+yxl1XW/46bpieIA4xp1BNAk1MTDDo2n/1x8vfCFhDpwWCifCvtgwVoHHU2tHUEQdhNte9LXTUN501a2jOSk/ASlMpJiagJhMstXfw6pCCELq4V+/oG/sP7+Ilglo8qgkiGhiUU2CB5zozNQcNJwRk4z2JLWrOr745d8N6ewArW9Z+j0xV12UFxwyer9z26vq1AC++OBl/Bp2dBemteS/pWLpbauIPx5xeCObzyjF7B3dz9aDbUoDcrndg/TNzNhgauS4BBrxqpKNrbmlt4ei6zGXpWtgWkiaX80YWGlqB1WPSBZpxg9JgkW16akENeM2ZuZE/38V+xTLHyVXWs+bMf8CeumbPUeLHuHhY753+don94/hbOr/LLEgHuMRMPH4M7K1x6vGpeTXNRL3yrYYwCZMS2McXro3nRjyms8Dp2qc9L48HfB6BJ+Dx9GC2aukMrxmgmT2zHDaY6Vh6YnFHCvjxAPjFjz4Ios6UocdKgJDQtLiIsHcTX4hfozE54lZDaiC8uTb1WAgksGtSIdGOMefRACsyFo1ASYtBYmcqxBUsZmzQ6HOzIcLUrgy21SiMj4REsfDpxs+C4WMkgDyA3AG9UkJp41bu0ZdQ7OAee/3rUa8XUb7INiG4klVKGRWxWab9j+tdcLOXuq4seX3kKKRvZTbDmvnWKgsrP3EPe2pkCaEFgjyoFPRLCbJ+2IpvTuzSeFR2U6W4ZHYTIFo421HHepXzUutJrn/ba06xDRbEYBm8/giuKC0dVQdnL6QgDQ+cHA1XB9YwEf+YAqcEoJithtAQb1cz9fzMQWLpahSzG0CfVwklvJbUrSx8sl3aKmucNsnOq1nRDHBBYTzGd0p7EfQVuQDghtDnvuJirdaOObXk+loUbatCnNh9YbwMONK9uKYLHNGkZgRdzz28PS3lTHxgHisqDloXXYIMj0ZvHXR2iGGsV0thBk54JTujhXl8GTsR5rkoFTpfy3y9Os7JlMuWN0I37xKkR+sFgr5dyFwdHYfUF5q9ULp1XTbPzarcg+GKwyGkFq4eoFpsOQu5BE7LhwcvQUluRBTEsJPToJJZAo3sMEAafy7NJuYlHUNnuA4UqwumjWt0dB0tXCFRkUl1x6m3aklGc1ycD/Fq2MxQ1IaVAXXEtGQFulqXxhf6vMzg+LM2HqXKw87L5q/Ci2bLAL+oc+VM/tCyqM2BTEKoTK8yOCrOBAxQeKJhl/gXDq14Ld4uqqyA1QnJeKtXA/GdXril1MzA57S9+pkocrACsOWLZC0AGTahnm6uz+i4xJvGvLywldcJqfyILqk8DToVI0twFqsXeM9Er/ZkZDkX4iIEaVAqZXctMAGYP/e66R4rq1Bud0a6gYgtZfdDhmmfspl7RLeiWU9mC0pNo2q4/II4yH0+YeWGbTaZNFq5TMZjSkVhuXJCXIRp/Gto5ZM/Rp4HfQajmVEGED9m0+gyj2oXCrCHtae0mxUBUMaTK94ANwTwxIQQlgBAGK+Gb1oK5obU58fC9A24UaHGNiUrl4FtZQy89V8fx1qYPbOKFmqg3wnuKA/qL/fweDZUL9rpGDoKODijZKiZvWZLWLtYAdgjVeiuBDUxwFOZNI3MdXWe1jNxS1Wm1p3l7m44y3H+vFnW6tSN3k+DHpYGcCrsp/5/Cbgp0vmfnhr9CdoeirpKvIZHAAAAAElFTkSuQmCC";
            const trayImgBase64 = trayImgDataURL.replace(/^data:image\/(png|jpg|jpeg|webp);base64,/, "");

            // sticker image
            let imgBase64 = getCanvasStickerDataURL().replace(/^data:image\/(png|jpg|jpeg|webp);base64,/, "");
            if (isPlatform('ios')) {
              const { resultImgBase64, trayIconBase64 } = await StickerService.convertImgToWebp(imgBase64);
              imgBase64 = resultImgBase64;
            }
            store.commit('updateSticker', { stickerId: sticker.value.id, updatedObj: {
              outputPhotoBase64: `data:image/webp;base64,${imgBase64}`,
              updatedAt: new Date(),
            } });
            
            //await saveCanvas();
            
            const emojis = ["❤", "🥰", "⭐️"];
            
            // filter invalid sticker photos
            let validStickers = [ ...store.state.userStickers ];
            const objIndex = validStickers.findIndex((s => s.id == currStickerId));
            if (objIndex >= 0) validStickers[objIndex].outputPhotoBase64 = `data:image/webp;base64,${imgBase64}`;
            await Promise.all(validStickers.map(async (us) => {
              if (us.id != currStickerId) {
                const outputPhoto = us.outputPhoto || '';
                if (outputPhoto.startsWith('http')) {
                  const base64Data: any = await getBase64FromUrl(outputPhoto, true);
                  us.outputPhotoBase64 = base64Data.replace(/^data:image\/(png|jpg|jpeg|webp);base64,/, "data:image/webp;base64,");
                }
              }
            }));
            const isValidBase64Size = (base64Str: any) => {
              const stringLength = base64Str.length - 'data:image/webp;base64,'.length;
              const sizeInBytes = 4 * Math.ceil((stringLength / 3))*0.5624896334383812;
              const sizeInKb = sizeInBytes/1000;
              return sizeInKb < 100; // must be less than 100KB
            };
            validStickers = validStickers.filter(us => (us.outputPhotoBase64 && us.outputPhotoBase64.startsWith('data:image/webp;base64,U') && isValidBase64Size(us.outputPhotoBase64))).sort((a, b) => {
              const timeA = new Date(a.updatedAt), timeB = new Date(b.updatedAt);
              return timeA > timeB ? -1 : (timeA < timeB ? 1 : 0);
            }).slice(0, 30); // at most 30 stickers (from latest to oldest)

            const isValidBase64Dimension = (base64Str: string) => {
              return new Promise((resolve) => {
                const img = new Image(); // Decode the base64 string into an image and check its dimensions
                img.onload = () => {
                  const isValidDimensions = img.width === OUTPUT_STICKER_WIDTH && img.height === OUTPUT_STICKER_WIDTH;
                  resolve(isValidDimensions);
                };
                img.onerror = () => {
                  resolve(false); // Image failed to load, consider invalid
                };
                img.src = base64Str;
              });
            };
            validStickers = await Promise.all(validStickers.map(async (s) => {
              if (s.id != currStickerId) {
                if (!isValidBase64Dimension(s.outputPhotoBase64)) {
                  const { resultImgBase64, } = await StickerService.convertImgToWebp(s.outputPhotoBase64);
                  s.outputPhotoBase64 = resultImgBase64;
                }
              }
              return s;
            }));

            // default sticker pack data for iOS
            let data: any = {
              ios_app_store_link: 'https://itunes.apple.com/app/id1571465949',
              android_play_store_link: 'https://play.google.com/store/apps/details?id=com.mlolpet.MallLolPet',
              identifier: `MallLolPetStickers`, // identifier for iOS
              //animated_sticker_pack: false,
              name: `Mall Lol Pet Stickers`,
              publisher: `Mall LOL Pet`,
              publisher_website: `https://mlol.pet`,
              tray_image: trayImgBase64,
              /*
              stickers: [
                {
                  "image_data": "",
                  "emojis": emojis,
                },
                {
                  "image_data": "",
                  "emojis": emojis,
                },
                {
                  "image_data": "",
                  "emojis": emojis,
                },
              ]
              */
              stickers: padStickers(validStickers.map(us => ({
                "image_data": us.outputPhotoBase64.replace(/^data:image\/(png|jpg|jpeg|webp);base64,/, ""),
                "emojis": emojis,
              }))),
            };
            if (isPlatform("android")) {
              const trayImgFileName = `tray_mlp.png`;
              await writeFileToDevice(trayImgFileName, trayImgDataURL);
              
              // include all user stickers in the pack
              let stickerIdx = 1;
              const stickers = [], fileNameSuffix = 'MLP_sticker.webp';
              /*validStickers = [
                { outputPhotoBase64: "data:image/webp;base64," }
              ];*/
              for (const us of validStickers) {
                const fileName = `${(stickerIdx++).toString().padStart(2, "0")}_${fileNameSuffix}`;
                await writeFileToDevice(fileName, us.outputPhotoBase64);
                stickers.push({
                  "image_file": fileName,
                  "emojis": emojis
                });
              }

              const nextImgDataVer = Number(store.state.user.stickerImageDataVer) + 1;
              data = {
                "identifier": "MallLolPet-stickers",
                "name": "Mall Lol Pet Stickers",
                "contents": {
                  "android_play_store_link": "https://play.google.com/store/apps/details?id=com.mlolpet.MallLolPet",
                  "ios_app_store_link": "https://itunes.apple.com/app/id1571465949",
                  "sticker_packs": [
                    {
                      "identifier": "MallLolPet-stickers",
                      "name": "Mall Lol Pet Stickers",
                      "publisher": "Mall LOL Pet",
                      "tray_image_file": trayImgFileName,
                      "image_data_version": nextImgDataVer.toString(),
                      "avoid_cache": false,
                      "publisher_email": "",
                      "publisher_website": "https://mlol.pet",
                      "privacy_policy_website": "",
                      "license_agreement_website": "",
                      //"animated_sticker_pack": true,
                      "stickers": padStickers(stickers)
                    }
                  ]
                }
              };
              store.dispatch('updateUserStickerImgVer', nextImgDataVer);
              console.log(data);
            }
            const json = JSON.stringify(data);
            console.log("Adding to WhatsApp stickers...");
            
            try {
              window["WhatsAppStickers"].addToWhatsApp(json, () => {
                presentToast(t('successAddStickerToWhatsApp'));
                console.log("success");
                loading.dismiss();
              }, (e: any) => {
                presentToast(t('failedAddStickerToWhatsApp'));
                console.log("error");
                console.log(e);
                loading.dismiss();
              });
            } catch (e) {
              presentToast(t('failedAddStickerToWhatsApp'));
              console.error(e);
              loading.dismiss();
            }
          } catch (e) {
            presentToast(t('failedAddStickerToWhatsApp'));
            console.error(e);
            loading.dismiss();
          }
          if (serverReqFlag) autoSaveCanvas(); // save canvas once anyway
        }, 'sticker-preview-alert');
      },

      // Image Filters,
      enterFilterMode: () => {
        if (serverReqFlag) clearTimeout(serverReqFlag); // stop auto update
        getCanvasDataURL();
        const canvasOutputBase64 = getCanvasDataURL("image/webp", OUTPUT_STICKER_WIDTH*2 / canvas.sceneWidth);
        loadHTMLImg(canvasOutputBase64, (img: any) => {
          canvas.filteringImg.image = img;
          setTimeout(() => {
            canvas.filteringImg.scale = (Math.min(canvas.sceneWidth / img.naturalWidth, canvas.sceneHeight / img.naturalHeight));
          }, 100);
        });
        canvas.isFiltering = true;
        setPanelModalBreakpoint(0.3); 
      },
      leaveFilterMode: () => {
        canvas.isFiltering = false;
        if (serverReqFlag) autoSaveCanvas();
      },
      applyFilter: (filter: any) => {
        canvas.selectedFilter = filter;
        const targetNode = filterStageRef.value.getStage().findOne(`#filteringImg`);
        if (targetNode) {
          let filters = [];
          if (filter == photoFilters.pixelate) filters = [Konva.Filters.Pixelate];
          else if (filter == photoFilters.posterize) filters = [Konva.Filters.Posterize];
          targetNode.filters(filters);
          const pixelRatio = Math.round(((OUTPUT_STICKER_WIDTH / canvas.sceneWidth)+ Number.EPSILON) * 10000000000000) / 10000000000000;
          targetNode.cache({ pixelRatio: Math.min(pixelRatio, 1) });
        }
      },

      onMaterialCategoryChanged: () => {
        canvas.selectedPanelShape = { colors: [] };
      },
      clearCanvasBackground: () => {
        canvas.backgroundImage = null;
        canvas.backgroundMaterialId = null;
        canvas.backgroundImageScale = 1;
        canvas.backgroundImageX = 0;
        canvas.backgroundImageY = 0;
        canvas.isSelectingBackground = false;
        autoSaveCanvas();
      },
    }
  }
}
